import { Typography } from 'antd';
import dayjs from 'dayjs';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Cell,
  Label,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { ReactComponent as BackButton } from '../../../../Assets/sib/back_button.svg';
import styles from './index.module.css';

import { ReactComponent as IncomingArrow } from '../../../../Assets/incoming_arrow.svg';
import { ReactComponent as Investment } from '../../../../Assets/investmentIcon.svg';
import { ReactComponent as NoTxn } from '../../../../Assets/noTxn.svg';
import { ReactComponent as OutgoingArrow } from '../../../../Assets/outgoing_arrow.svg';
import { ReactComponent as RightArrow } from '../../../../Assets/sib/rightCircle-arrow.svg';
import { ReactComponent as FaqIcon } from '../../../../Assets/sib/FaqIcon.svg';

import { lowerCase, startCase } from 'es-toolkit';
import { AppContext } from '../../../../App';
import { EVENTCODES, pushEventToParent } from '../../../../Utils/eventUtils';
import { ROUTES } from '../../../../Utils/routes';
import {
  formatToINR,
  reversedCategoriesMapping,
  transactionCategoriesLogos,
} from '../../../../Utils/utils';
import { getImagePath } from '../../../../Utils/assetUtils';
import CategoryTransactions from '../CategoryTransactions';
import { getAltJourney } from '../../../../Utils/storageUtils';
import { altJourneyType } from '../../../../Utils/types';

/** Parse 'MMM-YY' string (e.g. 'Sep-25') to a sortable number (year*12 + monthIndex). */
function parseMonthYear(monthYear: string): number {
  const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const [mon, yr] = monthYear.split('-');
  const monthIndex = MONTH_NAMES.indexOf(mon);
  const year = 2000 + parseInt(yr, 10);
  return year * 12 + monthIndex;
}

/** Merge TOP5 monthly txns only for entries that fall within the last N calendar months from today.
 *  monthCount = 0 means "All" — no date filtering. */
function aggregateMonthlyTxns(
  monthly: any[] | undefined,
  monthCount: 0 | 1 | 3 | 6,
): any[] {
  if (!monthly?.length) return [];
  const slice =
    monthCount === 0
      ? monthly
      : monthly.filter((m: any) => {
          // monthCount calendar months inclusive of the current one, so
          // "Past 1 Month" is just this month, "Past 3 Months" is this
          // month plus the 2 before it, etc.
          const cutoff = parseMonthYear(
            dayjs().subtract(monthCount - 1, 'month').format('MMM-YY'),
          );
          return parseMonthYear(m.monthYear) >= cutoff;
        });
  const byCategory: Record<string, any> = {};

  for (const month of slice) {
    if (!month?.txns?.length) continue;
    for (const txn of month.txns) {
      const key = txn.category ?? 'Unknown';
      if (!byCategory[key]) {
        byCategory[key] = {
          ...txn,
          amount: Number(txn.amount) || 0,
          noOfTxn:
            txn.noOfTxn != null ? Number(txn.noOfTxn) || 0 : undefined,
          txnRefIdList: [...(txn.txnRefIdList ?? [])],
        };
      } else {
        const cur = byCategory[key];
        cur.amount = (Number(cur.amount) || 0) + (Number(txn.amount) || 0);
        if (txn.noOfTxn != null) {
          cur.noOfTxn =
            (Number(cur.noOfTxn) || 0) + (Number(txn.noOfTxn) || 0);
        }
        cur.txnRefIdList = [
          ...(cur.txnRefIdList ?? []),
          ...(txn.txnRefIdList ?? []),
        ];
      }
    }
  }

  return Object.values(byCategory);
}

const RANGE_OPTIONS: { value: 1 | 3 | 6; label: string }[] = [
  { value: 1, label: 'Past 1 Month' },
  { value: 3, label: 'Past 3 Months' },
  { value: 6, label: 'Past 6 Months' },
];

const CategoryAnalysis: React.FC<{ defaultDataKey: string; onOpenFAQ?: () => void }> = ({
  defaultDataKey = 'CREDIT',
  onOpenFAQ,
}) => {
  const { isAmountVisible }: any = useContext(AppContext);
  const { data, setIsCategoryAnalysisShown, currentRoute, setCurrentRoute, setPreviousRoute, setBackButtonHandler }: any =
    useContext(AppContext);

  const [currentDataKey, setCurrentDataKey] = useState(defaultDataKey);
  const [showCategoryTransactions, setCategoryTransaction] = useState(false);
  const [selectedCategory, setSelecetedCategory] = useState<string | undefined>(
    undefined,
  );
  const [transactionRefIdList, setTransactionRefIdList] = useState([]);



  const [selectedRangeMonths, setSelectedRangeMonths] = useState< 1 | 3 | 6>(1);

  const monthlyForCurrentKey = useMemo(() => {
    const dataLocal = data.current.TOP5FUNDTRANSFER?.overall;
    if (
      dataLocal?.[currentDataKey]?.monthly &&
      Array.isArray(dataLocal[currentDataKey].monthly)
    ) {
      return dataLocal[currentDataKey].monthly as any[];
    }
    return [];
  }, [data, currentDataKey]);

  const topcategories = useMemo(
    () =>
      aggregateMonthlyTxns(monthlyForCurrentKey, selectedRangeMonths).sort(
        (a: any, b: any) => (Number(b.amount) || 0) - (Number(a.amount) || 0),
      ),
    [monthlyForCurrentKey, selectedRangeMonths],
  );

  const amount = useMemo(() => {
    return topcategories
      .map((txn: any) => txn.amount)
      .reduce((a: number, b: number) => a + b, 0);
  }, [topcategories]);

  const accountIds = useMemo(() => {
    const ids = data.current.ACCOUNT_DETAILS?.accountProfileList
      ?.map((account: any) => account.account.id)
      .filter((id: any) => id !== null);
    return ids;
  }, [data.current.ACCOUNT_DETAILS]);

  const sheetRef: any = useRef({});

  const handleImageError = (event: any) => {
    event.target.src = getImagePath('images/categoryIcons/OTHERS.svg');
  };

  useEffect(() => {
    setBackButtonHandler(() => {
      return () => {
        if (showCategoryTransactions) {
          setCategoryTransaction(false);
          setSelecetedCategory(undefined);
        } else {
          pushEventToParent({ eventCode: EVENTCODES.BACK_BUTTON_CATEGORIES });
          setIsCategoryAnalysisShown(false);
        }
      };
    });
  }, [showCategoryTransactions]);

  const renderTransactionCategory = (txns: any) => {
    return (
      <div className={styles.txnList}>
        {txns.length > 0 ? (
          txns.map((txn: any) => (
            <div
              className={styles.trasactionContainer}
              onClick={() => {
                if (txn.masterCategoryCode && txn.txnRefIdList.length > 0) {
                  setTransactionRefIdList(txn.txnRefIdList);
                  setCategoryTransaction(true);
                  setSelecetedCategory(txn.masterCategoryCode);
                }
              }}
            >
              <div style={{ display: 'flex', gap: 8, width: '100%' }}>
                <img
                  src={getImagePath(
                    'images/categoryIcons/' +
                      transactionCategoriesLogos[
                        reversedCategoriesMapping[txn.category.toLowerCase()]
                      ] +
                      '.svg',
                  )}
                  className={styles.categoryIcon}
                  onError={handleImageError}
                />
                <div className={styles.txnRight}>
                  <div className={styles.txnDetails}>
                    <div className={styles.txnNameRow}>
                      <Typography.Text className={styles.topTransactionName}>
                        {txn.category}
                      </Typography.Text>
                      <Typography.Text className={styles.txnAmount}>
                        {formatToINR(txn.amount.toString(), isAmountVisible)}
                      </Typography.Text>
                    </div>

                    <Typography.Text className={styles.txnPercentage}>
                      {((txn.amount / amount!) * 100).toFixed(2)}%
                    </Typography.Text>
                  </div>

                  <RightArrow />
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className={styles.emptyState}>
            <NoTxn />
            <span className={styles.emptyText}>No transaction found</span>
          </div>
        )}
      </div>
    );
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#D90368'];

const PieTooltip = ({ active, payload, amount }: any) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0];
  const pct = amount > 0 ? `${((entry.value / amount) * 100).toFixed(2)}%` : '';
  return (
    <div style={{
      background: '#ffffff',
      border: '1px solid rgba(5,47,95,0.15)',
      borderRadius: 8,
      padding: '8px 12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: entry.payload.fill, flexShrink: 0 }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: '#052f5f' }}>{entry.payload.category}</span>
      </div>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#052f5f', marginTop: 2 }}>{pct}</div>
    </div>
  );
};

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerLeft}>
            <BackButton
              onClick={() => {
                if (showCategoryTransactions) {
                  setCategoryTransaction(false);
                  setSelecetedCategory(undefined);
                } else {
                  pushEventToParent({ eventCode: EVENTCODES.BACK_BUTTON_CATEGORIES });
                  setIsCategoryAnalysisShown(false);
                }
              }}
              className={styles.backButton}
            />
            <Typography.Text className={styles.headerTitle}>
              Account Summary
            </Typography.Text>
          </div>
          <FaqIcon
            className={styles.headerIcon}
            onClick={() => {
              if (onOpenFAQ) {
                onOpenFAQ();
              } else {
                setPreviousRoute(currentRoute);
                setCurrentRoute(ROUTES.SIB_FAQ);
              }
            }}
          />
        </div>
      </div>

      {showCategoryTransactions ? (
        <div className={styles.content}>
          <div
            className={`${styles.categoryTransaction} ${
              getAltJourney() === altJourneyType.YESONLINE
                ? styles.categoryTransaction_yb
                : ''
            }`}
          >
            <Typography.Text className={styles.categoryTitle}>
              {startCase(selectedCategory!)}
            </Typography.Text>
            <Typography.Text className={styles.categorySubtitle}>
              Here is the list of {lowerCase(selectedCategory!)} transactions
              for your accounts
            </Typography.Text>

            <CategoryTransactions
              selectedAccounts={accountIds}
              transactionIdList={transactionRefIdList}
            />
          </div>
        </div>
      ) : (
        <div className={styles.content}>
          <div className={styles.categorySelector} ref={sheetRef}>
            <div
              className={`${currentDataKey === 'CREDIT' ? styles.incomingTagSelected : styles.incomingTag} ${styles.tabIconRow}`}
              onClick={() => {
                setCurrentDataKey('CREDIT');
              }}
            >
              <IncomingArrow
                style={{
                  color: currentDataKey === 'CREDIT' ? 'var(--section-title)' : '#020300',
                }}
              />
              <Typography.Text
                style={{
                  fontSize: 14,
                  fontWeight: currentDataKey === 'CREDIT' ? 700 : 600,
                  color: currentDataKey === 'CREDIT' ? 'var(--section-title)' : '#020300',
                }}
              >
                Incoming
              </Typography.Text>
            </div>
            <div
              className={`${currentDataKey === 'EXPENSE' ? styles.expenseTagSelected : styles.expenseTag} ${styles.tabIconRow}`}
              onClick={() => {
                setCurrentDataKey('EXPENSE');
              }}
            >
              <OutgoingArrow
                style={{
                  color: currentDataKey === 'EXPENSE' ? 'var(--section-title)' : '#020300',
                }}
              />

              <Typography.Text
                style={{
                  fontSize: 14,
                  fontWeight: currentDataKey === 'EXPENSE' ? 700 : 600,
                  color: currentDataKey === 'EXPENSE' ? 'var(--section-title)' : '#020300',
                }}
              >
                Expenses
              </Typography.Text>
            </div>
            <div
              className={`${currentDataKey === 'INVESTMENT' ? styles.investmentTagSelected : styles.investmentTag} ${styles.tabIconRow}`}
              onClick={() => {
                setCurrentDataKey('INVESTMENT');
              }}
            >
              <Investment
                style={{
                  color:
                    currentDataKey === 'INVESTMENT' ? 'var(--section-title)' : '#020300',
                }}
              />
              <Typography.Text
                style={{
                  fontSize: 14,
                  fontWeight: currentDataKey === 'INVESTMENT' ? 700 : 600,
                  color:
                    currentDataKey === 'INVESTMENT' ? 'var(--section-title)' : '#020300',
                }}
              >
                Investments
              </Typography.Text>
            </div>
          </div>
          {topcategories.length > 0 && (
            <div className={styles.chartCard}>
              <ResponsiveContainer width={'100%'} height={300}>
                <PieChart width={1000} height={300}>
                  <Pie
                    data={topcategories}
                    innerRadius={70}
                    outerRadius={90}
                    fill='#8884d8'
                    dataKey='amount'
                    labelLine={false}
                    isAnimationActive={false}
                  >
                    {topcategories.map((entry: any, index: any) => (
                      <Cell
                        key={`cell-${entry.category}`}
                        fill={COLORS[index % COLORS.length]}
                        style={{ outline: 'none' }}
                      />
                    ))}
                    <Label
                      value='Top Categories'
                      position='center'
                      fill='#000000'
                      fontSize={14}
                    />
                  </Pie>
                  <Tooltip content={<PieTooltip amount={amount} />} />
                  <Legend
                    align='center' // Align the legend in the center horizontally
                    verticalAlign='bottom' // Position the legend at the bottom of the chart
                    layout='horizontal' // Set the layout to horizontal
                    iconType='circle'
                    iconSize={6}
                    wrapperStyle={{ wordWrap: 'break-word' }}
                    content={({ payload }: any) => {
                      const halfIndex = Math.ceil(payload.length / 2);
                      const firstHalf = payload.slice(0, halfIndex);
                      const secondHalf = payload.slice(halfIndex);

                      return (
                        <div className={styles.legendContainer}>
                          {firstHalf.map((entry: any, index: any) => (
                            <div className={styles.legendItem}>
                              <div
                                style={{
                                  minWidth: '5px',
                                  height: '5px',
                                  backgroundColor: entry.color,
                                  marginRight: '5px',
                                  borderRadius: '50%',
                                  flexShrink: 0,
                                }}
                              />
                              <Typography.Text className={styles.legendLabel}>
                                {entry.payload.category}
                              </Typography.Text>
                              <Typography.Text className={styles.legendPercent}>
                                {amount > 0
                                  ? `${((entry.payload.amount / amount) * 100).toFixed(2)}%`
                                  : ''}
                              </Typography.Text>
                            </div>
                          ))}

                          {secondHalf.map((entry: any, index: any) => (
                            <div className={styles.legendItem}>
                              <div
                                style={{
                                  minWidth: '5px',
                                  height: '5px',
                                  backgroundColor: entry.color,
                                  marginRight: '5px',
                                  borderRadius: '50%',
                                  flexShrink: 0,
                                }}
                              />
                              <Typography.Text className={styles.legendLabel}>
                                {entry.payload.category}
                              </Typography.Text>
                              <Typography.Text className={styles.legendPercent}>
                                {amount > 0
                                  ? `${((entry.payload.amount / amount) * 100).toFixed(2)}%`
                                  : ''}
                              </Typography.Text>
                            </div>
                          ))}
                        </div>
                      );
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}{' '}
          <div className={styles.tagsContainer}>
            {RANGE_OPTIONS.map((opt) => (
              <div
                key={opt.value}
                className={`${styles.tag} ${
                  selectedRangeMonths === opt.value ? styles.selectedTag : ''
                }`}
                onClick={() => setSelectedRangeMonths(opt.value)}
              >
                <Typography.Text className={styles.tagLabel}>
                  {opt.label}
                </Typography.Text>
              </div>
            ))}
          </div>
          <div
            className={`${styles.trasactionsContainer} ${
              getAltJourney() === altJourneyType.YESONLINE
                ? styles.trasactionsContainer_yb
                : ''
            }`}
          >
            {renderTransactionCategory(topcategories)}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryAnalysis;
